import { useState, useEffect, useMemo, useCallback } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { useCart } from '../../hooks/useCart';
import { CartItemCard } from '../../components/cart/CartItemCard';
import { CartSummary } from '../../components/cart/CartSummary';
import { ShareButton } from '../../components/cart/ShareButton';
import { MinOrderAlert } from '../../components/cart/MinOrderAlert';
import { ShippingTierProgress } from '../../components/cart/ShippingTierProgress';
import { PacksInCart } from '../../components/cart/PacksInCart';
import { FrequentlyBoughtTogether } from '../../components/cart/FrequentlyBoughtTogether';
import { EmptyState } from '../../components/cart/EmptyState';
import type { AdminOverrides, CartReviewData } from '../../types/cart';
import { getCSRFToken } from '../../utils/csrf';
import { route } from '../../utils/route';

declare const dataLayer: unknown[];

interface CartProps {
  allowsDiscountCode: boolean;
  isInstagramBrowser: boolean;
  hasFreeDeliveryWelcome: boolean;
  isAdminImpersonating: boolean;
}

export default function Cart({
  allowsDiscountCode,
  isInstagramBrowser,
  hasFreeDeliveryWelcome,
  isAdminImpersonating,
}: CartProps) {
  const [data, setData] = useState<CartReviewData | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const dataUrl = route('api.cart');
  const checkoutUrl = isInstagramBrowser ? route('checkout.show') : route('orders.confirm');

  const loadCart = useCallback(async () => {
    const response = await fetch(dataUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'include',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Error al cargar el carrito');
    const cartData: CartReviewData = await response.json();
    setData(cartData);
    setRefreshKey(k => k + 1);
    return cartData;
  }, [dataUrl]);

  useEffect(() => {
    loadCart()
      .then((cartData) => {
        if (cartData.items.length > 0) {
          dataLayer.push({ ecommerce: null });
          dataLayer.push({
            event: 'view_cart',
            ecommerce: {
              currency: 'ARS',
              value: cartData.items.reduce((sum, item) => sum + item.total, 0),
              coupon: cartData.discountCode || undefined,
              items: cartData.items.map(item => ({
                item_name: item.title,
                item_id: item.id,
                price: item.price,
                item_category: item.category_name,
                item_variant: item.format_name,
                quantity: item.quantity,
              })),
            },
          });
        }
      })
      .catch(err => {
        console.error('Error loading cart:', err);
        setError('Error al cargar el carrito. Por favor recarga la página.');
      });
  }, [loadCart]);

  // Refetch cart when another device/tab modifies it
  useEffect(() => {
    const handler = () => { loadCart(); };
    window.addEventListener('cart-sync-external', handler);
    return () => window.removeEventListener('cart-sync-external', handler);
  }, [loadCart]);

  if (error) {
    return (
      <MainLayout>
        <Head title="Carrito">
          <meta name="robots" content="noindex" />
        </Head>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center py-10 text-red-600">
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 py-2.5 px-5 bg-green-600 text-white border-none rounded-md cursor-pointer"
            >
              Reintentar
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <Head title="Carrito">
          <meta name="robots" content="noindex" />
        </Head>
        <div className="max-w-6xl mx-auto px-4">
          <div className="min-h-[50vh] flex items-center justify-center">
            <div className="text-center">
              <svg className="w-8 h-8 text-green-600 mx-auto mb-4 animate-spin" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-500">Cargando carrito...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Head title="Carrito">
        <meta name="robots" content="noindex" />
      </Head>
      <div className="max-w-6xl mx-auto px-4">
        <CartContent
          key={refreshKey}
          data={data}
          allowsDiscountCode={allowsDiscountCode}
          checkoutUrl={checkoutUrl}
          hasFreeDeliveryWelcome={hasFreeDeliveryWelcome}
          isAdminImpersonating={isAdminImpersonating}
          onRefresh={loadCart}
        />
      </div>
    </MainLayout>
  );
}

function AdminOverridesPanel({ overrides, onRefresh }: { overrides: AdminOverrides; onRefresh: () => Promise<CartReviewData> }) {
  const [bypassMinOrder, setBypassMinOrder] = useState(overrides.bypassMinOrder);
  const [bypassDelivery, setBypassDelivery] = useState(overrides.bypassDelivery);
  const [loading, setLoading] = useState(false);

  const toggle = async (field: 'bypass_min_order' | 'bypass_delivery', value: boolean) => {
    setLoading(true);
    try {
      await fetch('/api/cart/admin-override', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({ field, value }),
      });
      await onRefresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-4 p-3.5 bg-amber-50 border border-amber-300 rounded-lg">
      <div className="flex items-center gap-2 mb-2.5">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-700 shrink-0">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <span className="font-semibold text-amber-800 text-sm">Panel Admin — Impersonando usuario</span>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={bypassMinOrder}
            disabled={loading}
            onChange={e => { setBypassMinOrder(e.target.checked); toggle('bypass_min_order', e.target.checked); }}
            className="w-4 h-4 accent-amber-600"
          />
          <span className="text-sm text-amber-800">Omitir monto mínimo de compra</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={bypassDelivery}
            disabled={loading}
            onChange={e => { setBypassDelivery(e.target.checked); toggle('bypass_delivery', e.target.checked); }}
            className="w-4 h-4 accent-amber-600"
          />
          <span className="text-sm text-amber-800">Omitir costo de envío</span>
        </label>
      </div>
    </div>
  );
}

function CartContent({
  data,
  allowsDiscountCode,
  checkoutUrl,
  hasFreeDeliveryWelcome,
  isAdminImpersonating,
  onRefresh,
}: {
  data: CartReviewData;
  allowsDiscountCode: boolean;
  checkoutUrl: string;
  hasFreeDeliveryWelcome: boolean;
  isAdminImpersonating: boolean;
  onRefresh: () => Promise<CartReviewData>;
}) {
  const {
    items,
    minOrder,
    subtotal,
    discountCode,
    discountAmount,
    discountPercentage,
    discountableItems,
    discountError,
    deliveryPrice,
    total,
    itemCount,
    shareLink,
    hasUnfulfilledMinimum,
    isEmpty,
    isLoading,
    updatingItemId,
    subscription,
    replacementCreditDiscount,
    removeItem,
    updateQuantity,
    applyDiscount,
    previewDiscount,
    confirmDiscount,
    cancelPreview,
    previewData,
    clearDiscount,
    shareCart,
    getItemUniqueId,
    discountInfo,
    removeItemsBySource,
    shippingTiers,
    currentShippingTier,
    nextShippingTier,
    hasFreeDelivery,
  } = useCart(data, onRefresh);

  const suggestedCartCount = useMemo(
    () => items.filter(i => i.source === 'suggested_cart').length,
    [items],
  );

  const discountableSet = useMemo(() => new Set(discountableItems), [discountableItems]);

  const canCheckout = useMemo(() => {
    return itemCount > 0 && !hasUnfulfilledMinimum;
  }, [itemCount, hasUnfulfilledMinimum]);

  const handleApplyDiscount = useCallback(async (code: string) => {
    await applyDiscount(code);
  }, [applyDiscount]);

  const handlePreviewDiscount = useCallback(async (code: string) => {
    await previewDiscount(code);
  }, [previewDiscount]);

  const handleConfirmDiscount = useCallback(async () => {
    await confirmDiscount();
  }, [confirmDiscount]);

  const handleClearDiscount = useCallback(async () => {
    await clearDiscount();
  }, [clearDiscount]);

  const handlePackRemoved = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  const handleFbtAddToCart = useCallback(async (productId: number, formatId: number, quantity: number) => {
    await fetch(route('api.cart.add_item'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCSRFToken(),
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
      body: JSON.stringify({ id: productId, format_id: formatId, quantity, upselled: true, source: data.upsellSource ?? 'fbt' }),
    });

    // GA4 e-commerce funnel (standard add_to_cart)
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'ARS',
        items: [{ item_id: productId, quantity }],
      },
    });

    // PostHog upsell tracking
    dataLayer.push({
      event: 'upsell_add_to_cart',
      upsell_source: data.upsellSource ?? 'fbt',
      variant: data.upsellVariant ?? 'control',
      product_id: productId,
      format_id: formatId,
      quantity,
    });

    await onRefresh();
  }, [onRefresh, data.upsellSource, data.upsellVariant]);

  if (isEmpty) {
    return <EmptyState />;
  }

  return (
    <div className="py-6 pb-[220px] md:pb-16 lg:pb-20">
      <div className="flex items-center justify-between mb-6 md:mb-8 px-1">
        <div className="flex items-baseline gap-2.5">
          <h1 className="text-2xl md:text-[28px] lg:text-[32px] font-bold text-gray-800 m-0">Tu Carrito</h1>
          {/* Lets the customer check the count against their own list (#65). */}
          <span className="text-sm text-gray-500 whitespace-nowrap">
            {items.length} {items.length === 1 ? 'producto' : 'productos'}
          </span>
        </div>
        <ShareButton shareLink={shareLink} onShare={shareCart} />
      </div>

      <div className="mb-4">
        {isAdminImpersonating && data.adminOverrides && (
          <AdminOverridesPanel overrides={data.adminOverrides} onRefresh={onRefresh} />
        )}

        {hasFreeDeliveryWelcome && (
          <div className="flex items-center gap-2.5 py-3 px-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm mb-3">
            <svg className="shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="8" width="18" height="4" rx="1" />
              <path d="M12 8v13" />
              <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
              <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
            </svg>
            Tenes envio gratis en tu primer pedido!
          </div>
        )}

        {hasUnfulfilledMinimum && (!shippingTiers?.length || hasFreeDelivery) && (
          <MinOrderAlert minOrder={minOrder} currentTotal={subtotal} />
        )}
      </div>

      <div className="flex flex-col md:flex-row md:items-start gap-5">
        <div className="flex-1 min-w-0">
          {suggestedCartCount > 0 && (
            <div className="flex items-center justify-between gap-3 py-2.5 px-4 bg-green-50 border border-green-200 rounded-lg text-sm mb-3">
              <span className="text-green-800">
                {suggestedCartCount} producto{suggestedCartCount > 1 ? 's' : ''} agregado{suggestedCartCount > 1 ? 's' : ''} desde carrito sugerido
              </span>
              <button
                onClick={() => removeItemsBySource('suggested_cart')}
                disabled={isLoading}
                className="text-green-700 hover:text-green-900 font-medium whitespace-nowrap bg-transparent border-none cursor-pointer disabled:opacity-50"
              >
                Quitar
              </button>
            </div>
          )}

          <div className="hidden md:block rounded-xl border border-gray-200 bg-white overflow-hidden mb-3">
            {items.map((item, index) => {
              const itemKey = getItemUniqueId(item.id, item.format_id);
              const giftQty = (item.source === 'referral_gift' && data.referralGift?.format_id === item.format_id)
                ? data.referralGift.gift_quantity
                : undefined;
              return (
                <div key={itemKey}>
                  {index > 0 && <div className="border-t border-gray-100 mx-4" />}
                  <CartItemCard
                    item={item}
                    onRemove={removeItem}
                    onUpdateQuantity={updateQuantity}
                    isUpdating={updatingItemId === itemKey}
                    discountPercentage={discountableSet.has(itemKey) ? discountPercentage : null}
                    giftQuantity={giftQty}
                  />
                </div>
              );
            })}

            {shippingTiers && shippingTiers.length > 0 && !hasFreeDelivery && (
              <>
                <div className="border-t border-gray-100 mx-4" />
                <div className="px-4 py-3">
                  <ShippingTierProgress
                    tiers={shippingTiers}
                    subtotal={subtotal}
                    currentTier={currentShippingTier ?? null}
                    nextTier={nextShippingTier ?? null}
                  />
                </div>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="md:hidden rounded-xl border border-gray-200 bg-white overflow-hidden mb-3">
            {items.map((item, index) => {
              const itemKey = getItemUniqueId(item.id, item.format_id);
              const giftQty = (item.source === 'referral_gift' && data.referralGift?.format_id === item.format_id)
                ? data.referralGift.gift_quantity
                : undefined;
              return (
                <div key={itemKey}>
                  {index > 0 && <div className="border-t border-gray-100 mx-3.5" />}
                  <CartItemCard
                    item={item}
                    onRemove={removeItem}
                    onUpdateQuantity={updateQuantity}
                    isUpdating={updatingItemId === itemKey}
                    discountPercentage={discountableSet.has(itemKey) ? discountPercentage : null}
                    giftQuantity={giftQty}
                  />
                </div>
              );
            })}

            {shippingTiers && shippingTiers.length > 0 && !hasFreeDelivery && (
              <>
                <div className="border-t border-gray-100 mx-3.5" />
                <div className="px-3.5 py-3">
                  <ShippingTierProgress
                    tiers={shippingTiers}
                    subtotal={subtotal}
                    currentTier={currentShippingTier ?? null}
                    nextTier={nextShippingTier ?? null}
                  />
                </div>
              </>
            )}
          </div>

          {data.frequentlyBoughtTogether?.length > 0 && (
            <FrequentlyBoughtTogether
              products={data.frequentlyBoughtTogether}
              onAddToCart={handleFbtAddToCart}
              variant={data.upsellVariant ?? 'control'}
              source={data.upsellSource ?? 'fbt'}
            />
          )}

          <PacksInCart
            cartItems={items}
            onPackRemoved={handlePackRemoved}
          />
        </div>

        <div className="contents md:block md:w-[340px] md:shrink-0">
          <CartSummary
            subtotal={subtotal}
            discountCode={discountCode}
            discountAmount={discountAmount}
            discountError={discountError}
            discountInfo={discountInfo}
            deliveryPrice={deliveryPrice}
            total={total}
            allowsDiscountCode={allowsDiscountCode}
            isLoading={isLoading}
            onApplyDiscount={handleApplyDiscount}
            onPreviewDiscount={handlePreviewDiscount}
            onConfirmDiscount={handleConfirmDiscount}
            onCancelPreview={cancelPreview}
            previewData={previewData}
            onClearDiscount={handleClearDiscount}
            checkoutUrl={checkoutUrl}
            canCheckout={canCheckout}
            itemCount={itemCount}
            subscription={subscription}
            replacementCreditDiscount={replacementCreditDiscount}
          />
        </div>
      </div>

      <div className="block md:hidden mb-2">
        <a
          href={route('products.index')}
          className="block w-full py-3 px-5 bg-transparent text-green-600 text-center no-underline text-[15px] font-medium border border-green-600 rounded-lg hover:bg-green-50"
        >
          Volver al mercado
        </a>
      </div>

    </div>
  );
}
